/**
 * Formatting utilities
 */

/**
 * Format amount in paise to INR currency string
 * @param paise Amount in paise (smallest unit)
 * @returns Formatted currency string (e.g., "₹1,234.56")
 */
export const formatCurrency = (paise: number): string => {
  const rupees = paise / 100;
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(rupees);
};

/**
 * Format amount in paise to a number string
 * @param paise Amount in paise
 * @returns Formatted number string (e.g., "1,234.56")
 */
export const formatAmount = (paise: number): string => {
  const rupees = paise / 100;
  return new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(rupees);
};

/**
 * Parse a rupee string to paise
 * @param rupees Rupee amount (e.g., "1234.56")
 * @returns Amount in paise
 */
export const parseToPaise = (rupees: string | number): number => {
  const amount = typeof rupees === 'string' ? parseFloat(rupees) : rupees;
  if (isNaN(amount)) return 0;
  return Math.round(amount * 100);
};

/**
 * Format date to display string
 * @param date Date string or Date object
 * @returns Formatted date string (e.g., "Jan 15, 2024")
 */
export const formatDate = (date: string | Date): string => {
  const d = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat('en-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(d);
};

/**
 * Format date to short format
 * @param date Date string or Date object
 * @returns Formatted date string (e.g., "15/01/2024")
 */
export const formatDateShort = (date: string | Date): string => {
  const d = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat('en-IN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d);
};

/**
 * Format date for input fields (YYYY-MM-DD)
 * @param date Date string or Date object
 * @returns Formatted date string for input
 */
export const formatDateForInput = (date: string | Date): string => {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toISOString().split('T')[0];
};

/**
 * Format date with time
 * @param date Date string or Date object
 * @returns Formatted date time string
 */
export const formatDateTime = (date: string | Date): string => {
  const d = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat('en-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d);
};

/**
 * Format relative time (e.g., "2 days ago")
 * @param date Date string or Date object
 * @returns Relative time string
 */
export const formatRelativeTime = (date: string | Date): string => {
  const d = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffMinutes = Math.floor(diffMs / (1000 * 60));

  if (diffMinutes < 1) return 'Just now';
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return formatDate(d);
};

/**
 * Format percentage
 * @param value Decimal value (0-1 or 0-100)
 * @param isDecimal Whether the value is a decimal (0-1)
 * @returns Formatted percentage string
 */
export const formatPercentage = (value: number, isDecimal = false): string => {
  const pct = isDecimal ? value * 100 : value;
  return `${pct.toFixed(1)}%`;
};

/**
 * Truncate string with ellipsis
 * @param str String to truncate
 * @param maxLength Maximum length
 * @returns Truncated string
 */
export const truncate = (str: string, maxLength: number): string => {
  if (str.length <= maxLength) return str;
  return `${str.substring(0, maxLength - 3)}...`;
};

/**
 * Get initials from name
 * @param name Full name
 * @returns Initials (e.g., "JD" for "John Doe")
 */
export const getInitials = (name: string): string => {
  return name
    .split(' ')
    .map((part) => part.charAt(0).toUpperCase())
    .slice(0, 2)
    .join('');
};
